import { useQuery } from '@tanstack/react-query'
import { readMonthCalendar as request } from './requests/read'
import { type MonthCalendarResponse } from './types'
import { readMonthCalendar } from '.'

type UseReadMonthCalendarParams = {
  enabled?: boolean
  /** Last day of the window, `yyyy-MM-dd`. Inclusive. */
  endDate: string
  /** First day of the window, `yyyy-MM-dd`. */
  startDate: string
  /** Narrows the window to one story. The Story Show calendar sets this. */
  storyId?: string
  workspaceUuid: string
}

type UseReadMonthCalendarOptions<T = MonthCalendarResponse> =
  UseReadMonthCalendarParams & {
    select?: (data: MonthCalendarResponse) => T
  }

/**
 * The month window in one document.
 *
 * The whole padded grid is fetched and narrowing happens in the client, so
 * choosing a filter never costs a round trip. Only the window and the story
 * scope are part of the request, and therefore of the cache key.
 *
 * `staleTime` is nought: people edit what this draws, so a cached answer is
 * shown at once and checked against the server every time the month is drawn
 * again. The workspace default of five minutes would keep an edit off the grid
 * until it ran out.
 */
export const useReadMonthCalendar = <T = MonthCalendarResponse>({
  enabled,
  endDate,
  select,
  startDate,
  storyId,
  workspaceUuid,
}: UseReadMonthCalendarOptions<T>) => {
  const query = {
    filter: {
      scheduled_at: { gteq: startDate, lteq: endDate },
      ...(storyId === undefined ? {} : { story_id: storyId }),
    },
  }

  return useQuery({
    enabled,
    queryKey: readMonthCalendar.queryKey({
      queryParams: query,
      workspaceUuid,
    }),
    queryFn: () =>
      request({
        params: { path: { workspace_id: workspaceUuid }, query },
      }),
    select,
    staleTime: 0,
  })
}
